import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { createNeedHelpTicket } from "../../lib/needHelpApi";
import { findScriptInTicketFields } from "../../lib/ticketInputGuard";
import styles from "./NeedHelpModal.module.css";

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_error) {
    return "";
  }
};

const NeedHelpTicketModal = ({ isOpen, userId, onClose, onSuccess }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    subject: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const attachmentInputRef = useRef(null);
  const subjectRef = useRef(null);
  const descriptionRef = useRef(null);

  // Scripts / code (SQL, HTML, XML, JavaScript ...) are not allowed in the
  // subject or description. Checked while typing, so the user sees it at once.
  const scriptError = findScriptInTicketFields({
    Subject: subject,
    Description: description,
  });
  const visibleError = scriptError || error;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, submitting]);

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
    if (fieldErrors.subject) {
      setFieldErrors((current) => ({ ...current, subject: "" }));
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
    if (fieldErrors.description) {
      setFieldErrors((current) => ({ ...current, description: "" }));
    }
  };

  const handleAttachmentChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;

    const oversized = selectedFiles.find((file) => file.size > 5 * 1024 * 1024);
    if (oversized) {
      setError(`"${oversized.name}" is over the 5MB attachment limit.`);
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) =>
        !["image/png", "image/jpeg", "application/pdf"].includes(file.type),
    );
    if (invalidFile) {
      setError(`"${invalidFile.name}" is not a supported file. Choose PNG, JPG, or PDF.`);
      return;
    }

    const isDuplicate = (candidate, existing) =>
      candidate.name === existing.name &&
      candidate.size === existing.size &&
      candidate.lastModified === existing.lastModified &&
      candidate.type === existing.type;
    const additions = selectedFiles.filter(
      (candidate, index) =>
        !attachments.some((existing) => isDuplicate(candidate, existing)) &&
        !selectedFiles
          .slice(0, index)
          .some((prior) => isDuplicate(candidate, prior)),
    );

    if (attachments.length + additions.length > 10) {
      setError("Maximum 10 attachments only.");
      return;
    }

    setError(
      additions.length < selectedFiles.length
        ? "Duplicate files were already selected and were not added."
        : "",
    );
    setAttachments([...attachments, ...additions]);
  };

  const removeAttachment = (fileToRemove) => {
    setAttachments((currentFiles) =>
      currentFiles.filter((file) => file !== fileToRemove),
    );
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const nextFieldErrors = {
      subject: subject.trim() ? "" : "Subject is required.",
      description: description.trim() ? "" : "Description is required.",
    };
    setFieldErrors(nextFieldErrors);

    if (nextFieldErrors.subject || nextFieldErrors.description) {
      (nextFieldErrors.subject ? subjectRef : descriptionRef).current?.focus();
      return;
    }

    if (scriptError) {
      setError(scriptError);
      return;
    }

    const token = getToken();
    if (!userId || !token) {
      setError("Please sign in to submit a support ticket.");
      return;
    }

    setSubmitting(true);
    try {
      await createNeedHelpTicket({
        subject: subject.trim(),
        description: description.trim(),
        userId,
        attachments,
        token,
      });

      setSubject("");
      setDescription("");
      setAttachments([]);
      setError("");
      setFieldErrors({ subject: "", description: "" });
      onSuccess?.();
    } catch (submitError) {
      console.error("Failed to submit Need Help ticket", submitError);
      setError(
        submitError.message ||
          "Could not submit your ticket. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.ticketModalOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div className={styles.ticketModalStack}>
        <section
          className={styles.ticketModalCard}
          role="dialog"
          aria-modal="true"
          aria-labelledby="need-help-ticket-title"
        >
          <h2 id="need-help-ticket-title" className={styles.ticketModalTitle}>
            Create Support Ticket
          </h2>

          <form className={styles.ticketForm} onSubmit={handleSubmit} noValidate>
            {/* Scrolls when the content (e.g. many attachments) is taller than
                the popup. The popup itself keeps its size. */}
            <div className={styles.ticketFormBody}>
              <label className={styles.ticketLabel} htmlFor="need-help-ticket-subject">
                Subject
              </label>
              <input
                ref={subjectRef}
                id="need-help-ticket-subject"
                className={styles.ticketInput}
                type="text"
                maxLength={100}
                value={subject}
                onChange={handleSubjectChange}
                placeholder="What do you need help with?"
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.subject)}
                aria-describedby={
                  fieldErrors.subject ? "need-help-ticket-subject-error" : undefined
                }
              />
              {fieldErrors.subject && (
                <p
                  id="need-help-ticket-subject-error"
                  className={styles.ticketFieldError}
                  role="alert"
                >
                  {fieldErrors.subject}
                </p>
              )}

              <label className={styles.ticketLabel} htmlFor="need-help-ticket-description">
                Description
              </label>
              <textarea
                ref={descriptionRef}
                id="need-help-ticket-description"
                className={styles.ticketTextarea}
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Describe the issue..."
                rows={4}
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.description)}
                aria-describedby={
                  fieldErrors.description
                    ? "need-help-ticket-description-error"
                    : undefined
                }
              />
              {fieldErrors.description && (
                <p
                  id="need-help-ticket-description-error"
                  className={styles.ticketFieldError}
                  role="alert"
                >
                  {fieldErrors.description}
                </p>
              )}

              <label className={styles.ticketLabel} htmlFor="need-help-ticket-attachment">
                Attachments <span>(optional, up to 5 files; PNG/JPG/PDF, 5MB each)</span>
              </label>
              <div className={styles.ticketFilePicker}>
                <input
                  ref={attachmentInputRef}
                  id="need-help-ticket-attachment"
                  className={styles.ticketFileInput}
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  multiple
                  onChange={handleAttachmentChange}
                  aria-label="Choose ticket attachments"
                />
                <button
                  type="button"
                  className={styles.ticketFileChoose}
                  onClick={() => attachmentInputRef.current?.click()}
                  disabled={submitting}
                >
                  Choose files
                </button>
                <span className={styles.ticketFileCount} aria-live="polite">
                  {attachments.length === 0
                    ? "No files chosen"
                    : `${attachments.length} ${attachments.length === 1 ? "file" : "files"}`}
                </span>
              </div>
              {attachments.length > 0 && (
                <div className={styles.ticketFileList}>
                  {attachments.map((file, index) => (
                    <div
                      className={styles.ticketFileItem}
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                    >
                      <span className={styles.ticketFileName}>{file.name}</span>
                      <button
                        type="button"
                        className={styles.ticketFileRemove}
                        onClick={() => removeAttachment(file)}
                        aria-label={`Remove ${file.name}`}
                        title={`Remove ${file.name}`}
                        disabled={submitting}
                      >
                        <X size={14} strokeWidth={2.2} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Always stuck to the bottom of the popup. */}
            <div className={styles.ticketFormFooter}>
              {visibleError && (
                <p className={styles.ticketError} role="alert">
                  {visibleError}
                </p>
              )}

              <button
                type="submit"
                className={styles.ticketSubmit}
                disabled={submitting || Boolean(scriptError)}
              >
                {submitting ? "Submitting..." : "Submit ticket"}
              </button>
            </div>
          </form>
        </section>

        <button
          type="button"
          className={styles.ticketModalClose}
          onClick={onClose}
          disabled={submitting}
          aria-label="Close support ticket modal"
          title="Close"
        >
          <X size={23} strokeWidth={2.6} aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default NeedHelpTicketModal;
